import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import deleteSiteHealthConfig from "../http/deleteSiteHealthConfig";
import getSiteHealthConfig, {
  SiteHealthConfig,
} from "../http/getSiteHealthConfig";
import putSiteHealthConfig, {
  PutSiteHealthConfigRequest,
} from "../http/putSiteHealthConfig";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

export const useSiteHealthConfig = (siteId: string | undefined) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  const queryClient = useQueryClient();

  const query = useQuery<SiteHealthConfig | null>(
    ["siteHealthConfig", siteId],
    async () => {
      const token = await getAuthTokenBlocking(getToken, relogin);
      return getSiteHealthConfig(token, siteId!);
    },
    {
      enabled: !!siteId,
      staleTime: 60_000,
    }
  );

  // Invalidate every cache that depends on the health config — most notably
  // the per-site PR (which uses the GPR rule for RYG bands) and the global
  // health overview aggregate (which renders all sites' PR + GPR). Without
  // these, a fresh GPR rule wouldn't show up until the staleTime expires.
  const invalidateConfigDependents = () => {
    queryClient.invalidateQueries(["siteHealthConfig", siteId]);
    queryClient.invalidateQueries(["site", siteId, "health_pr"]);
    queryClient.invalidateQueries(["healthOverview"]);
  };

  const updateConfig = useMutation(
    async (config: PutSiteHealthConfigRequest) => {
      const token = await getAuthTokenBlocking(getToken, relogin);
      return putSiteHealthConfig(token, siteId!, config);
    },
    {
      onSuccess: invalidateConfigDependents,
      onError: (error: Error) => {
        alert(`Failed to update health config: ${error.message}`);
      },
    }
  );

  const deleteConfig = useMutation(
    async () => {
      const token = await getAuthTokenBlocking(getToken, relogin);
      return deleteSiteHealthConfig(token, siteId!);
    },
    {
      onSuccess: invalidateConfigDependents,
      onError: (error: Error) => {
        alert(`Failed to delete health config: ${error.message}`);
      },
    }
  );

  return {
    healthConfig: query,
    updateConfig,
    deleteConfig,
  };
};
